import { BlogArticle } from '../types';

// 考試時間管理 → CTA: /module/exam
const article: BlogArticle = {
  slug: 'exam-time-management-tips',
  date: '2026-08-31',
  readingMinutes: 4,
  tags: ['考試技巧', '時間管理', 'DSE'],

  locales: {
    zhHant: {
      title: '考試時間管理：唔好再俾「唔夠時間」拖低你嘅分',
      excerpt: '識做但做唔完，係最可惜嘅失分。學識呢 4 個時間管理技巧，等你可以喺考試入面發揮返真正實力。',
      keywords: '考試技巧, 時間管理, exam tips, DSE, 做卷技巧',
      contentHtml: `
        <p>考試最唔抵嘅失分，唔係唔識，而係<strong>識但做唔切</strong>。時間管理唔係天份，係一套可以練嘅技術。以下 4 個技巧，由開卷嗰刻用到最後一分鐘。</p>

        <h2>1. 開卷先做「時間預算」</h2>
        <p>睇卷嗰 5 分鐘唔好齋睇題目。將總時間按分數比例分配落每條題目，例如 2 小時嘅卷、一條佔 20 分（總分 100）嘅題目，就值大約 24 分鐘。<strong>寫低每部分嘅「死線」</strong>，到時到候就要走。</p>

        <h2>2. 先易後難，但要有紀律</h2>
        <p>先攞晒啲「快分」係啱嘅，但好多人衰喺「唔捨得走」。定一條死規矩：<strong>一條題目諗超過 2 分鐘都冇頭緒，即刻跳過</strong>，做埋其他先返轉頭。</p>

        <h2>3. 長題目用「點列式」答題</h2>
        <p>時間唔夠嗰陣，長題目唔好再寫完整段落。用 point form 將得分點列清楚 —— 考官改卷係搵關鍵字嘅，點列清楚反而更易攞分。</p>

        <div class="tip-box">
          <strong>💡 貼士：</strong>預留最後 5–10 分鐘做檢查，重點檢查：有冇漏題、單位有冇寫、MC 有冇填錯格。呢啲「低級錯誤」每年累積嘅失分超乎你想像。
        </div>

        <h2>4. 平時練習就要計時</h2>
        <p>時間感唔係考場先練出嚟嘅。平時做 past paper 就應該：</p>
        <ul>
          <li>全程計時，唔好俾自己「做多陣」</li>
          <li>記低每部分實際用咗幾耐，同預算對比</li>
          <li>邊部分成日超時，就針對性練嗰部分嘅速度</li>
        </ul>

        <h2>時間分配參考</h2>
        <table>
          <thead>
            <tr><th>階段</th><th>行動</th></tr>
          </thead>
          <tbody>
            <tr><td>開卷 5 分鐘</td><td>睇題目 + 定時間預算</td></tr>
            <tr><td>頭 70% 時間</td><td>先易後難，完成大部分題目</td></tr>
            <tr><td>跟住 20%</td><td>攻難題 + 補漏</td></tr>
            <tr><td>最後 10%</td><td>檢查漏題、單位、MC 格</td></tr>
          </tbody>
        </table>

        <h2>想練習計時做卷？</h2>
        <p>用 AI 生成模擬試卷，配合計時練習，考試前就練好時間感：</p>
        <p><a class="blog-cta-button" href="/module/exam">🎓 免費生成模擬試卷 →</a></p>

        <blockquote>考試唔單止考你識咩，仲考你幾快攞到分。</blockquote>
      `,
    },

    zhHans: {
      title: '考试时间管理：别再让「时间不够」拖低你的分数',
      excerpt: '会做但做不完，是最可惜的失分。学会这 4 个时间管理技巧，让你可以在考试里发挥出真正实力。',
      keywords: '考试技巧, 时间管理, exam tips, DSE, 做卷技巧',
      contentHtml: `
        <p>考试最不值得的失分，不是不会，而是<strong>会但来不及做</strong>。时间管理不是天分，是一套可以练的技术。以下 4 个技巧，从开卷那刻用到最后一分钟。</p>

        <h2>1. 开卷先做「时间预算」</h2>
        <p>看卷那 5 分钟不要只看题目。把总时间按分数比例分配到每道题目，例如 2 小时的卷、一道占 20 分（总分 100）的题目，就值大约 24 分钟。<strong>写下每部分的「死线」</strong>，到时间就要走。</p>

        <h2>2. 先易后难，但要有纪律</h2>
        <p>先拿完「快分」是对的，但很多人败在「不舍得走」。定一条死规矩：<strong>一道题目想超过 2 分钟都没头绪，立刻跳过</strong>，做完其他再回头。</p>

        <h2>3. 长题目用「点列式」答题</h2>
        <p>时间不够的时候，长题目不要再写完整段落。用 point form 把得分点列清楚 —— 考官改卷是找关键字的，点列清楚反而更容易拿分。</p>

        <div class="tip-box">
          <strong>💡 提示：</strong>预留最后 5–10 分钟做检查，重点检查：有没有漏题、单位有没有写、MC 有没有填错格。这些「低级错误」每年累积的失分超乎你想象。
        </div>

        <h2>4. 平时练习就要计时</h2>
        <p>时间感不是考场才练出来的。平时做 past paper 就应该：</p>
        <ul>
          <li>全程计时，不要让自己「再做一会儿」</li>
          <li>记下每部分实际用了多久，和预算对比</li>
          <li>哪部分经常超时，就针对性练那部分的速度</li>
        </ul>

        <h2>时间分配参考</h2>
        <table>
          <thead>
            <tr><th>阶段</th><th>行动</th></tr>
          </thead>
          <tbody>
            <tr><td>开卷 5 分钟</td><td>看题目 + 定时间预算</td></tr>
            <tr><td>头 70% 时间</td><td>先易后难，完成大部分题目</td></tr>
            <tr><td>接着 20%</td><td>攻难题 + 补漏</td></tr>
            <tr><td>最后 10%</td><td>检查漏题、单位、MC 格</td></tr>
          </tbody>
        </table>

        <h2>想练习计时做卷？</h2>
        <p>用 AI 生成模拟试卷，配合计时练习，考试前就练好时间感：</p>
        <p><a class="blog-cta-button" href="/module/exam">🎓 免费生成模拟试卷 →</a></p>

        <blockquote>考试不只考你会什么，还考你多快拿到分。</blockquote>
      `,
    },

    en: {
      title: "Exam Time Management: Stop Losing Marks to the Clock",
      excerpt: "Knowing the answer but running out of time is the most painful way to lose marks. These 4 techniques let your real ability show up on paper.",
      keywords: 'exam tips, time management, DSE, exam technique, past papers',
      contentHtml: `
        <p>The most painful lost marks aren't from not knowing — they're from <strong>knowing but not finishing</strong>. Time management isn't talent; it's a trainable technique. These 4 tips cover everything from the first minute to the last.</p>

        <h2>1. Set a time budget before you start</h2>
        <p>Don't waste the reading time just reading. Split the total time by mark weighting: in a 2-hour, 100-mark paper, a 20-mark question is worth about 24 minutes. <strong>Write down the deadline for each section</strong> — and move on when you hit it.</p>

        <h2>2. Easy first — but with discipline</h2>
        <p>Banking the quick marks first is right, but most students fail at letting go. Set a hard rule: <strong>if a question gives you nothing after 2 minutes, skip it immediately</strong> and come back later.</p>

        <h2>3. Answer long questions in point form when time is short</h2>
        <p>Running low on time? Stop writing full paragraphs. List your marking points in point form — markers scan for keywords, and clean bullet points are easier to award marks to.</p>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Reserve the final 5–10 minutes for checking: missed questions, missing units, mis-filled MC bubbles. These "silly mistakes" cost more marks each year than you'd believe.
        </div>

        <h2>4. Practise with a timer all year</h2>
        <p>Your sense of time isn't built in the exam hall. When doing past papers:</p>
        <ul>
          <li>Time the whole paper — no "just five more minutes"</li>
          <li>Record how long each section actually took vs your budget</li>
          <li>Whichever section always overruns is the one to drill for speed</li>
        </ul>

        <h2>A sample time split</h2>
        <table>
          <thead>
            <tr><th>Phase</th><th>Action</th></tr>
          </thead>
          <tbody>
            <tr><td>First 5 min</td><td>Read questions + set time budget</td></tr>
            <tr><td>First 70%</td><td>Easy first, clear most of the paper</td></tr>
            <tr><td>Next 20%</td><td>Attack hard questions + fill gaps</td></tr>
            <tr><td>Final 10%</td><td>Check missed questions, units, MC grid</td></tr>
          </tbody>
        </table>

        <h2>Want timed practice papers?</h2>
        <p>Generate mock papers with AI and drill them under timed conditions, so your pacing is ready before the real thing:</p>
        <p><a class="blog-cta-button" href="/module/exam">🎓 Generate a free mock paper →</a></p>

        <blockquote>Exams don't just test what you know — they test how fast you can turn it into marks.</blockquote>
      `,
    },
  },
};

export default article;
